import type { PipelineRun } from '@/api/types'

import { draftPartsOf } from '@/draftParts'

import { LetterText, type MarkerRenderer } from './LetterText'

/**
 * A run's draft, as the letter and — kept apart from it — the drafter's notes.
 *
 * The server splits the draft (`draft_parts`, `server/app/text/letter.py`):
 * `draft_v5` closes every response with a `Grounds answered` table and an
 * `Attorney checklist` for the reviewer, and sometimes opens with a line of
 * citations. Shown inside the letter they read as part of it, and a tester's
 * export carried them to the office (TM-04). Here they sit under their own
 * heading, still on the page, still readable.
 *
 * A response from a server that predates `draft_parts` degrades to the whole
 * draft as the letter — never to nothing.
 */
export function DraftView({
  run,
  renderMarker,
  maxHeightClass = 'max-h-[560px]',
}: {
  run: Pick<PipelineRun, 'draft' | 'draft_parts'>
  /** How the letter's `[ATTORNEY: …]` markers are drawn. Notes use the default. */
  renderMarker?: MarkerRenderer
  maxHeightClass?: string
}) {
  const parts = draftPartsOf(run)
  const hasNotes = Boolean(parts.preamble || parts.grounds_answered || parts.checklist)

  return (
    <div className="flex flex-col gap-4">
      <div
        role="document"
        aria-label="The letter"
        className={`overflow-auto rounded-md bg-ground p-4 ${maxHeightClass}`}
      >
        <LetterText text={parts.letter} renderMarker={renderMarker} />
      </div>

      {hasNotes ? (
        <section
          aria-label="For the reviewer"
          className="flex flex-col gap-3 rounded-md border border-divider p-3"
        >
          <div className="flex flex-col gap-0.5">
            <h4 className="m-0 text-body font-semibold">For the reviewer</h4>
            <p className="m-0 text-micro text-muted">
              Notes the drafter wrote to check the letter against. They are not part of it: in
              the exported file they sit on the first page, with the worklist, and the pages after
              that are the letter alone.
            </p>
          </div>
          {parts.preamble ? (
            <div className="flex flex-col gap-1">
              <h5 className="m-0 text-meta font-semibold">Citations listed before the letter</h5>
              <LetterText text={parts.preamble} className="text-meta" />
            </div>
          ) : null}
          {parts.grounds_answered ? (
            <div className="flex flex-col gap-1">
              <h5 className="m-0 text-meta font-semibold">Grounds answered</h5>
              <LetterText text={parts.grounds_answered} className="text-meta" />
            </div>
          ) : null}
          {parts.checklist ? (
            <div className="flex flex-col gap-1">
              <h5 className="m-0 text-meta font-semibold">Attorney checklist</h5>
              <LetterText text={parts.checklist} className="text-meta" />
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  )
}
